import { useMemo } from 'react';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Card as GameCard } from '@/types/game';

interface ScoreCardProps {
  team: 'home' | 'away';
  teamName: string;
  teamColor: string;
  score: number;
  cards: GameCard[];
  onAdjustScore: (delta: number) => void;
  onIssueCard: () => void;
  onRemoveCard: (cardId: string) => void;
}

function ScoreCard({
  team,
  teamName,
  teamColor,
  score,
  cards,
  onAdjustScore,
  onIssueCard,
  onRemoveCard,
}: ScoreCardProps) {
  const teamCards = useMemo(
    () => cards.filter(c => c.team === team),
    [cards, team]
  );

  return (
    <Card className="p-6 bg-card border-l-8" style={{ borderLeftColor: teamColor }}>
      <div className="text-center space-y-3">
        <div className="text-sm font-medium text-muted-foreground truncate max-w-full">
          {teamName}
        </div>
        <div 
          className="text-6xl font-bold text-foreground leading-tight"
          role="status"
          aria-label={`${teamName} score: ${score}`}
        >
          {score}
        </div>
        
        {/* Large +1 Goal Button */}
        <Button
          size="lg"
          onClick={() => onAdjustScore(1)}
          className="w-full text-lg font-semibold"
          aria-label={`Add goal to ${teamName}`}
        >
          <Plus className="h-5 w-5 mr-2" />
          Goal
        </Button>
        
        {/* Smaller -1 Button */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAdjustScore(-1)}
          className="w-full"
          aria-label={`Remove goal from ${teamName}`}
        >
          <Minus className="h-4 w-4 mr-1" />
          Remove
        </Button>
        
        <Button
          variant="outline"
          className="w-full"
          onClick={onIssueCard}
          aria-label={`Issue card to ${teamName} team`}
        >
          Issue Card
        </Button>
        <div 
          className="flex flex-wrap gap-1 justify-center mt-2"
          role="list"
          aria-label={`${teamName} team cards`}
        >
          {teamCards.map(card => (
            <button
              key={card.id}
              onClick={() => onRemoveCard(card.id)}
              className="cursor-pointer hover:opacity-70 transition-opacity p-1.5 -m-1.5 rounded-md"
              aria-label={`Remove ${card.type} card for player ${card.playerNumber}`}
              title="Click to remove card"
            >
              <Badge
                variant={card.type === 'yellow' ? 'default' : 'destructive'}
                className={card.type === 'yellow' ? 'bg-yellow-500' : ''}
              >
                #{card.playerNumber}
                {card.reason?.toLowerCase().includes('second yellow') && (
                  <span className="ml-1 text-[10px] opacity-75">(2Y)</span>
                )}
              </Badge>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default ScoreCard;